import React from 'react';
import { IOverlayTrigger2Props, OverlayTrigger2 } from './OverlayTrigger2';
import { IPopover2Props, Popover2 } from './Popover2';
import { generateId } from '@adp-wfn/mdf-core';
import { SdfBox } from '@waypoint/react-components';

export type IStatusType = 'success' | 'info' | 'warning' | 'error' | 'default';
export type OverlayPopoverSize = undefined | 'xs' | 'sm' | 'md' | 'lg';

// This component accepts multiple children, but OverlayTrigger does not, so need to omit
// it from the IOverlayTriggerProps before we extend the interface.
export interface IOverlayPopover extends Omit<IOverlayTrigger2Props, 'children'> {
  className: string;
  children: any[];
  contentSize?: OverlayPopoverSize;
  disabled: boolean;
  type: IStatusType;
  useOverlayContent?: boolean;
}

export interface IMDFPopoverProps extends IPopover2Props {
  containerClassName?: string;
}

// This is a class because we need its type
export class MDFPopoverContent extends React.Component<IMDFPopoverProps, any> {
  render() {
    return <div>{this.props.children}</div>;
  }
}

// This is a class because we need its type
export class MDFOverlayContent extends React.Component<any, any> {
  render() {
    return <SdfBox>{this.props.children}</SdfBox>;
  }
}

export class MDFOverlayPopoverComponent extends React.Component<IOverlayPopover, any> {
  actionOverlay: any;
  popoverId: any;

  constructor(props: IOverlayPopover) {
    super(props);
    this.popoverId = generateId('MDFPopover');
  }

  popoverToggle = (event) => {
    if (this.props.onToggle) {
      this.props.onToggle(event);
    }
  };

  popoverClick = (event) => {
    if (this.props.onClick) {
      this.props.onClick(event);
    }
  };

  // close the popup if the target has a className of popup-closer
  closeOverlay = (e) => {
    const target = e.target;
    const parentTarget = e.target.offsetParent;

    if (target && (target.classList.contains('popup-closer') || parentTarget.classList.contains('popup-closer'))) {
      this.actionOverlay.hide();
    }
  };

  render() {
    let popover: MDFPopoverContent;
    let overlay: MDFOverlayContent;

    React.Children.forEach(this.props.children, (child: any): any => {
      if (child.type === MDFPopoverContent) {
        popover = child;
      }
      else if (child.type === MDFOverlayContent) {
        overlay = child;
      }
    });

    if (!popover || !overlay) {
      return null;
    }

    const { children, contentSize, ...rest } = this.props;

    // remove the containerClassName property before passing it to Popover component
    const { containerClassName, id, ...customProps } = popover.props;
    const popoverId = id || this.popoverId;

    let sizeToken: string;

    switch (contentSize) {
      case 'xs':
        sizeToken = 'max-w-xs';
        break;

      case 'sm':
        sizeToken = 'max-w-sm';
        break;

      case 'md':
        sizeToken = 'max-w-md';
        break;

      case 'lg':
        sizeToken = 'max-w-lg';
        break;

      default:
        if (containerClassName) {
          sizeToken = '';
        }
        else {
          sizeToken = 'max-w-xs';
        }

        break;
    }

    return (
      <div className={`mdf-overlay-popover ${this.props.className || ''}`}>
        {!this.props.disabled &&
          <OverlayTrigger2
            {...rest}
            overlay={
              <Popover2 {...customProps} id={popoverId} className={`mdf-overlay-popover ${containerClassName || ''} ${sizeToken} ${this.props.type ? `vdl-popover--${this.props.type}` : ''}`.trim()}>
                <div onClick={this.closeOverlay}>
                  {popover.props.children}
                </div>
              </Popover2>
            }
            ref={(actionOverlay) => this.actionOverlay = actionOverlay}
            onClick={this.popoverClick}
            onToggle={this.popoverToggle}
            hideOnWindowResize
            flip={true}
          >
            {this.props.useOverlayContent ? overlay : (overlay.props.children?.length ? overlay.props.children[0] : overlay.props.children)}
          </OverlayTrigger2>
        }
        {this.props.disabled && overlay.props.children}
      </div>
    );
  }
}
